import { router, useLocalSearchParams } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  useCreateNpoPaymentAccountDocuments,
  useGetNpoPaymentAccountSteps,
  useSaveNpoPaymentAccountSteps
} from '@/api/hooks/paymentAccountsHooks';
import DotsLoader from '@/components/DotsLoader';
import FormWrapper from '@/components/forms/FormWrapper';
import DocumentsForm from '@/components/forms/paymentNpoOnboarding/DocumentsForm';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { DocumentsData } from '@/lib/validations/NpoPaymentOnboarding';
import { i18n } from '@/utils/i18n';

const Documents = () => {
  const { npoId } = useLocalSearchParams<{
    npoId: string;
  }>();
  const { showSuccess } = useEndFlowModal();

  const { data: savedStepsData, isPending: savedStepsDataPending } = useGetNpoPaymentAccountSteps(npoId);
  const { mutateAsync: createAccount, isPending: isPendingCreateAccount } = useSaveNpoPaymentAccountSteps();
  const { mutateAsync: createPaymentAccountDocuments, isPending: createAccountPending } =
    useCreateNpoPaymentAccountDocuments();

  // ToDO: Check when prefilling with claim process files
  // const documentsData = savedStepsData?.files;
  // const initialData: DocumentsData = {
  //   voidedCheck: Platform.OS === 'web' ? (documentsData?.voidedCheck as File) : (documentsData?.voidedCheck as string),
  //   driverLicense:
  //     Platform.OS === 'web' ? (documentsData?.driverLicense as File) : (documentsData?.driverLicense as string),
  //   doc501c3: Platform.OS === 'web' ? (documentsData?.doc501c3 as File) : (documentsData?.doc501c3 as string),
  //   articlesOfIncorporation:
  //     Platform.OS === 'web'
  //       ? (documentsData?.articlesOfIncorporation as File)
  //       : (documentsData?.articlesOfIncorporation as string),
  //   bankStatements:
  //     Platform.OS === 'web' ? (documentsData?.bankStatements as File) : (documentsData?.bankStatements as string)
  // };

  const parseDocumentsData = (
    data: DocumentsData
  ): {
    voidedCheck: string | File;
    driverLicense: string | File;
    doc501c3: string | File;
    articlesOfIncorporation: string | File;
    bankStatements: string | File;
  } => {
    const parsedDocuments = {
      voidedCheck: Platform.OS === 'web' ? (data.voidedCheck as File) : (data.voidedCheck as string),
      driverLicense: Platform.OS === 'web' ? (data.driverLicense as File) : (data.driverLicense as string),
      doc501c3: Platform.OS === 'web' ? (data.doc501c3 as File) : (data.doc501c3 as string),
      articlesOfIncorporation:
        Platform.OS === 'web' ? (data.articlesOfIncorporation as File) : (data.articlesOfIncorporation as string),
      bankStatements: Platform.OS === 'web' ? (data.bankStatements as File) : (data.bankStatements as string)
    };

    return parsedDocuments;
  };

  const onCreateAccount = (data: DocumentsData) => {
    if (savedStepsData?.result?.allStepsSaved && !savedStepsData.result?.hasNuveiAccount) {
      createAccount(
        {
          nonProfitOrgId: npoId
        },
        {
          onSuccess: (_) => {
            createPaymentAccountDocuments(
              {
                body: { nonProfitOrgId: npoId },
                ...parseDocumentsData(data)
              },
              {
                onSuccess: () => {
                  showSuccess({
                    title: i18n.t('end_flow.success_title'),
                    message: i18n.t('npoPaymentOnboarding.success_message')
                  });
                  router.dismissAll();
                  router.push('/(app)/(bottomTabs)/profile/npoProfile/insights');
                }
              }
            );
          },
          onError: (e) => {
            console.error(e);
          }
        }
      );
    } else {
      createPaymentAccountDocuments(
        {
          body: { nonProfitOrgId: npoId },
          ...parseDocumentsData(data)
        },
        {
          onSuccess: () => {
            showSuccess({
              title: i18n.t('end_flow.success_title'),
              message:
                savedStepsData?.result?.hasNuveiAccount && savedStepsData.result?.docsUploadSaved
                  ? i18n.t('npoPaymentOnboarding.documents_updated')
                  : i18n.t('npoPaymentOnboarding.success_message')
            });
            router.dismissAll();
            router.push('/(app)/(bottomTabs)/profile/npoProfile/insights');
          }
        }
      );
    }
  };

  if (savedStepsDataPending) {
    return (
      <View style={style.loaderContainer}>
        <DotsLoader />
      </View>
    );
  }

  return (
    <SafeAreaView style={style.flexOne} edges={['bottom']}>
      <FormWrapper scrollEnable minHeightMinus={180}>
        <DocumentsForm
          // ToDO: Check saved files
          // initialData={savedStepsData?.files !== undefined && savedStepsData.files !== null ? initialData : undefined}
          isLoading={createAccountPending || isPendingCreateAccount}
          doneLabel={i18n.t('npoPaymentOnboarding.submit_label')}
          onDone={onCreateAccount}
        />
      </FormWrapper>
    </SafeAreaView>
  );
};

export default Documents;

const style = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  }
});
